import { upperFirst } from 'lodash-es'
import { type Dispatch, type SetStateAction, useMemo } from 'react'

import {
  DelimitedContent,
  FvDate,
  FvLinkButton,
  Icon,
} from '@fv/client-components'
import { totalShipmentWeight } from '@fv/client-core'
import type { FullShipment } from '@fv/client-types'
import { formatMoney } from '@fv/models/core'

import { DividedContent } from '@/components/DividedContent'
import { HasPermission } from '@/features/auth'
import { HideForVendor } from '@/features/auth/HasRole'
import { RefNumsEditor } from '@/features/ref-nums/RefNumsEditor'
import { LoadUserDisplay } from '@/features/shipment-list/LoadUserDisplay'
import { VendorMarkupDescription } from '@/features/shipment-list/VendorShipmentIndicator'
import { usePrettyNumber } from '@/hooks/settings'
import { useRateModifier } from '@/hooks/useRateModifier'
import { routes } from '@/routes'
import { mapPaymentTerms } from '@/utils/shipmentFuncs'

import { List, ListItem } from '../../List'
import type { PanelName } from '../LoadDetailPanel'

type ActivePanelHeaderNavProps = {
  isShowingRates: boolean
  setShowingRates: (s: boolean) => void
}

type ActivePanelHeaderBodyProps = {
  load: FullShipment
  setActivePanel: Dispatch<SetStateAction<PanelName>>
}

export const ActivePanelHeaderNav = ({
  isShowingRates,
  setShowingRates,
}: ActivePanelHeaderNavProps) => {
  if (!isShowingRates) return null

  return (
    <DividedContent>
      <FvLinkButton onClick={() => setShowingRates(false)} icon="arrow-to-left">
        Back
      </FvLinkButton>
      <h6 className="mb-0">
        <Icon icon="truck-moving" className="text-fv-gray-dark" />
        <span>Select new carrier / edit charges</span>
      </h6>
    </DividedContent>
  )
}

export const ActivePanelHeaderBody = ({
  load,
  setActivePanel,
}: ActivePanelHeaderBodyProps) => {
  const paymentTerms = mapPaymentTerms(load)
  const prettyNumber = usePrettyNumber()
  const rateMod = useRateModifier()

  const { equipment, selectedQuote } = load
  const amountDisplay = useMemo(() => {
    if (!selectedQuote) return formatMoney(0)
    const { amount } = rateMod(selectedQuote, load.workflow, load.exchangeRates)
    return formatMoney(amount, selectedQuote.currency)
  }, [load.workflow, load.exchangeRates, selectedQuote, rateMod])

  return (
    <div className="bg-fv-gray/15 border border-fv-gray p-4 space-y-1 mb-4 rounded-tr-[1rem]">
      <List fcp0 bare>
        <RefNumsEditor
          load={load}
          wrap={children => <ListItem>{children}</ListItem>}
        />
      </List>
      <p className="leading-6">
        <DelimitedContent delimiter={<> / </>}>
          {equipment.isHazardous && (
            <span>
              Hazardous {' / '}
              {upperFirst(equipment.description)}
            </span>
          )}
          {!!equipment.description && (
            <span>{upperFirst(equipment.description)}</span>
          )}
          <span>
            {prettyNumber(totalShipmentWeight(load))} {equipment.weightUOM}.
          </span>
        </DelimitedContent>
        <br />
        <span>
          Booked by <LoadUserDisplay {...load.bookedBy} /> /{' '}
          <FvDate val={load.bookedDate} utc format="MMM D, YYYY" />
        </span>
        <HasPermission name="view-rates">
          <br />
          <span className="flex gap-x-1">
            {paymentTerms && `${paymentTerms} / `}
            <HasPermission
              name="load-booker"
              fallback={<span>{amountDisplay}</span>}
            >
              <FvLinkButton
                theme="underlined"
                to={routes.rates(load, 'view')}
                onClick={e => {
                  if (load.workflow === 'truckload') {
                    e.preventDefault()
                    setActivePanel('quote')
                  }
                }}
              >
                <span>{amountDisplay}</span>
              </FvLinkButton>
            </HasPermission>
          </span>
          <HideForVendor>
            <VendorMarkupDescription noIcon load={load} label="Vendor price" />
          </HideForVendor>
        </HasPermission>
      </p>
    </div>
  )
}
